import React, { useState, useCallback } from 'react';

const GOLD = '#C9A84C';

function timeAgo(iso) {
  if (!iso) return 'Never';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
}

function LocationLabel({ city, region, country }) {
  const parts = [city, region, country].filter(Boolean);
  if (parts.length === 0) return 'Unknown Location';
  if (parts.length <= 2) return parts.join(', ');
  return `${city}, ${region}`;
}

function LocationCard({ location, isCurrent, onRelease, onRename, onMarkHome }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(location.label);
  const [releasing, setReleasing] = useState(false);

  const handleSaveLabel = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== location.label) {
      onRename(location.id, trimmed);
    }
    setEditing(false);
  }, [editValue, location.id, location.label, onRename]);

  const handleRelease = useCallback(async () => {
    setReleasing(true);
    try {
      await onRelease(location.id);
    } finally {
      setReleasing(false);
    }
  }, [location.id, onRelease]);

  return (
    <div style={{
      padding: 16,
      borderRadius: 16,
      background: isCurrent
        ? 'rgba(201, 168, 76, 0.08)'
        : 'rgba(255, 255, 255, 0.04)',
      border: isCurrent
        ? '1px solid rgba(201, 168, 76, 0.25)'
        : '1px solid rgba(255, 255, 255, 0.08)',
      transition: 'all 0.2s ease',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {/* Icon */}
        <div style={{
          width: 40, height: 40, borderRadius: 12, flexShrink: 0,
          background: location.isHome ? 'rgba(201, 168, 76, 0.12)' : 'rgba(255, 255, 255, 0.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {location.isHome ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {editing ? (
              <input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleSaveLabel}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveLabel(); if (e.key === 'Escape') setEditing(false); }}
                autoFocus
                maxLength={100}
                style={{
                  fontSize: 15, fontWeight: 600, color: 'var(--text-primary)',
                  background: 'rgba(255,255,255,0.08)', border: `1px solid ${GOLD}`,
                  borderRadius: 8, padding: '4px 8px', outline: 'none',
                  width: '100%', maxWidth: 200,
                }}
              />
            ) : (
              <span
                style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' }}
                onClick={() => { setEditValue(location.label); setEditing(true); }}
                title="Click to rename"
              >
                {location.label}
              </span>
            )}
            {location.isHome && (
              <span style={{
                fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                padding: '2px 8px', borderRadius: 6,
                background: 'rgba(201, 168, 76, 0.15)', color: GOLD,
              }}>
                Home
              </span>
            )}
            {isCurrent && (
              <span style={{
                fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                padding: '2px 8px', borderRadius: 6,
                background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e',
              }}>
                Current
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>
            <LocationLabel city={location.city} region={location.region} country={location.country} />
            {location.lastSeen && (
              <span style={{ marginLeft: 8, opacity: 0.7 }}>
                {timeAgo(location.lastSeen)}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {!location.isHome && (
            <button
              onClick={() => onMarkHome(location.id)}
              title="Mark as Home"
              style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'rgba(255,255,255,0.06)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', transition: 'background 0.15s',
                color: 'var(--text-tertiary)',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              </svg>
            </button>
          )}
          <button
            onClick={handleRelease}
            disabled={releasing}
            title="Release this location"
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'rgba(239, 68, 68, 0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: releasing ? 'wait' : 'pointer',
              transition: 'background 0.15s',
              opacity: releasing ? 0.5 : 1,
              color: '#ef4444',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LocationManager({
  isBlocked,
  locations,
  currentLocation,
  limit,
  message,
  onRelease,
  onRename,
  onMarkHome,
}) {
  if (!isBlocked) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99997,
      background: 'rgba(0, 0, 0, 0.85)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <div style={{
        maxWidth: 500, width: '100%',
        background: 'var(--bg-primary, #121212)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 24,
        padding: '32px 28px',
        boxShadow: '0 32px 100px rgba(0, 0, 0, 0.6)',
        maxHeight: '90vh', overflowY: 'auto',
      }}
        className="no-scrollbar"
      >
        {/* Header icon */}
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: 'rgba(201, 168, 76, 0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
        </div>

        <h2 style={{
          fontSize: 22, fontWeight: 700,
          color: 'var(--text-primary, #fff)',
          textAlign: 'center', marginBottom: 8,
        }}>
          Location Limit Reached
        </h2>

        <p style={{
          fontSize: 14, lineHeight: 1.6,
          color: 'var(--text-secondary, rgba(255,255,255,0.55))',
          textAlign: 'center', marginBottom: 8,
        }}>
          {message || `You're using Maestro from ${limit} locations. Release one to continue from this new location.`}
        </p>

        {currentLocation && (
          <p style={{
            fontSize: 13,
            color: 'var(--text-tertiary, rgba(255,255,255,0.4))',
            textAlign: 'center', marginBottom: 24,
          }}>
            New location: <strong style={{ color: 'var(--text-secondary)' }}>
              {currentLocation.city || currentLocation.region || currentLocation.country || 'Unknown'}
            </strong>
          </p>
        )}

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 16,
        }}>
          <span style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: 'var(--text-tertiary)',
          }}>
            Active Locations
          </span>
          <span style={{
            fontSize: 12, fontWeight: 600,
            color: locations.length >= limit ? '#ef4444' : 'var(--text-tertiary)',
          }}>
            {locations.length} of {limit}
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {locations.map((loc) => (
            <LocationCard
              key={loc.id}
              location={loc}
              isCurrent={false}
              onRelease={onRelease}
              onRename={onRename}
              onMarkHome={onMarkHome}
            />
          ))}
        </div>

        <p style={{
          fontSize: 12, color: 'var(--text-tertiary)',
          textAlign: 'center', marginTop: 20, lineHeight: 1.5,
        }}>
          Release a location you no longer need to continue.
          Unused locations expire automatically after 7 days.
        </p>
      </div>
    </div>
  );
}

export { LocationCard };
